<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"
            :loading-text="tableLoadingText"
            ajax-path="aws/route53/hostedzones"
            @ajaxResponse="handleAjaxResponse"
        />
        <app-modal
            :show.sync="showCreateModal"
            width="md:w-1/2 lg:w-1/3"
        >
            <template slot="header">Create Hosted Zone</template>
            <template slot="body">
                <div>A hosted zone is a container that holds information about how you want to route traffic for a domain, such as example.com, and its subdomains.</div>
                <app-input
                    v-model="createForm.name"
                    placeholder="Domain Name"
                    :error="createForm.errors.get('name')"
                    @updated="createForm.errors.clear('name')"
                    required
                    focus
                />
                <app-select
                    v-model="createForm.delegation_set_id"
                    placeholder="Delegation Set"
                    :error="createForm.errors.get('delegation_set_id')"
                    @updated="createForm.errors.clear('delegation_set_id')"
                    :options="delegationSets"
                    label-key="caller_reference"
                    value-key="id"
                />
                <app-input
                    v-model="createForm.caller_reference"
                    placeholder="Caller Reference"
                    :error="createForm.errors.get('caller_reference')"
                    @updated="createForm.errors.clear('caller_reference')"
                />
                <app-input
                    v-model="createForm.comment"
                    placeholder="Comment"
                    :error="createForm.errors.get('comment')"
                    @updated="createForm.errors.clear('comment')"
                />                
                <div class="text-right mt-5">
                    <app-button
                        class="btn-white"
                        @click="showCreateModal = false"
                        :disable="creatingHostedZone"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        @click="createHostedZone"
                        :loading="creatingHostedZone"
                    >Create</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showRecordsModal"
            width="lg:w-4/5"
        >
            <template slot="header">{{ selected.name }} Records</template>
            <template slot="body">
                <tabulator
                    :columns="[
                        {
                            title: 'Name',
                            field: 'name',
                            sorter: 'string',
                            minWidth: 200,
                        },
                        {
                            title: 'Type', 
                            field: 'type', 
                            sorter: 'string',
                            headerSort: false,
                            minWidth: 100,
                        },
                        {
                            title: 'Value',
                            field: 'value_for_human',
                            sorter: 'string',
                            minWidth: 300,
                            formatter: 'html',
                            variableHeight: true,
                        },
                        {
                            title: 'TTL',
                            field: 'ttl',
                            minWidth: 100,
                        },
                    ]"
                    :actions="recordsActions"
                    :ajax-params="recordsAjaxParams"
                    :refresh.sync="refreshRecordsTable"
                    ajax-path="aws/route53/hostedzones/records"
                />
            </template>
        </app-modal>
        <app-modal
            :show.sync="showCreateRecordModal"
            width="md:w-1/2 lg:w-1/3"
        >
            <template slot="header">Create Record Set</template>
            <template slot="body">
                <app-input
                    v-model="createRecordForm.name"
                    placeholder="Name"
                    :error="createRecordForm.errors.get('name')"
                    @updated="createRecordForm.errors.clear('name')"
                    required
                    focus
                />
                <app-select
                    v-model="createRecordForm.type"
                    placeholder="Type"
                    :error="createRecordForm.errors.get('type')"
                    @updated="createRecordForm.errors.clear('type')"
                    :options="recordTypeOptions"
                    label-key="label"
                    value-key="value"
                    required
                />
                <checkbox 
                    v-model="createRecordForm.alias"
                    class="mt-5"
                >This is an alias</checkbox>
                <div v-if="createRecordForm.alias === false">
                    <app-input
                        v-model="createRecordForm.ttl"
                        placeholder="TTL (seconds)"
                        :error="createRecordForm.errors.get('ttl')"
                        @updated="createRecordForm.errors.clear('ttl')"
                    />
                    <app-textarea
                        v-model="createRecordForm.resource_records"
                        placeholder="Value"
                        :error="createRecordForm.errors.get('resource_records')"
                        @updated="createRecordForm.errors.clear('resource_records')"
                        required
                    />
                    <div 
                        v-if="createRecordForm.type === 'A'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">IPv4 address. Enter multiple addresses on separate lines.</div>
                        <div class="flex-1 ml-5">Example:<br>
                            192.0.2.235<br>
                            198.51.100.234</div>
                    </div>
                    <div 
                        v-if="createRecordForm.type === 'CNAME'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">The domain name that you want to resolve to instead of the value in the Name field.</div>
                        <div class="flex-1 ml-5">
                            Example:<br>
                            www.example.com
                        </div>
                    </div>
                    <div 
                        v-if="createRecordForm.type === 'MX'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">A priority and a domain name that specifies a mail server. Enter multiple values on separate lines.</div>
                        <div class="flex-1 ml-5">
                            Format:<br>
                            [priority] [mail server host name]<br>
                            Example:<br>
                            10 mailserver.example.com.<br>
                            20 mailserver2.example.com.
                        </div>
                    </div>
                    <div 
                        v-if="createRecordForm.type === 'AAAA'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">IPv6 address. Enter multiple addresses on separate lines.</div>
                        <div class="flex-1 ml-5">
                            Example:<br>
                            2001:0db8:85a3:0:0:8a2e:0370:7334<br>
                            fe80:0:0:0:202:b3ff:fe1e:8329
                        </div>
                    </div>
                    <div 
                        v-if="createRecordForm.type === 'TXT'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">A text record. Enter multiple values on separate lines. Enclose text in quotation marks.</div>
                        <div class="flex-1 ml-5">
                            Example:<br>
                            "Sample Text Entries"<br>
                            "Enclose entries in quotation marks"
                        </div>
                    </div>
                    <div 
                        v-if="createRecordForm.type === 'PTR'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">The domain name that you want to return.</div>
                        <div class="flex-1 ml-5">
                            Example:<br>
                            www.example.com
                        </div>
                    </div>                    
                    <div 
                        v-if="createRecordForm.type === 'SRV'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">An SRV record. For information about SRV record format, refer to the applicable documentation. Enter multiple values on separate lines.</div>
                        <div class="flex-1 ml-5">
                            Format:<br>
                                [priority] [weight] [port] [server host name]<br>
                            Example:<br>
                                1 10 5269 xmpp-server.example.com.<br>
                                2 12 5060 sip-server.example.com.
                        </div>
                    </div>
                    <div 
                        v-if="createRecordForm.type === 'SPF'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">An SPF record. For information about SPF record format, refer to the applicable documentation. Enter multiple values on separate lines. Enclose values in quotation marks.</div>
                        <div class="flex-1 ml-5">
                            Example:<br>
                            "v=spf1 ip4:192.168.0.1/16-all"
                        </div>
                    </div>
                    <div 
                        v-if="createRecordForm.type === 'NAPTR'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">An NAPTR record. For information about NAPTR record format, refer to the applicable documentation. Enter multiple values on separate lines.</div>
                        <div class="flex-1 ml-5">
                            Format:<br>
                                [order] [preference] [flags] [services] [regexp] [replacement]<br>
                            Example:<br>
                                100 100 "U" "" "!^.*$!sip:info@bar.example.com!" .<br>
                                10 100 "S" "SIP+D2U" "" foo.example.com.
                        </div>
                    </div>
                    <div 
                        v-if="createRecordForm.type === 'CAA'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">Values that specify the certificate authorities that can issue certificates for this domain. Enter multiple values on separate lines.</div>
                        <div class="flex-1 ml-5">
                            Format:<br>
                                [flag] [tag] [value]<br>
                            Examples:<br>
                                0 issue "caa.example.com"<br>
                                0 issuewild ";"
                        </div>
                    </div>
                    <div 
                        v-if="createRecordForm.type === 'NS'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">The domain name of a name server. Enter multiple name servers on separate lines.</div>
                        <div class="flex-1 ml-5">
                            Examples:<br>
                                ns1.amazon.com<br>
                                ns2.amazon.org<br>
                                ns3.amazon.net<br>
                                ns4.amazon.co.uk
                        </div>
                    </div>
                    <div 
                        v-if="createRecordForm.type === 'SOA'"
                        class="text-gray-600 text-sm"
                    >
                        <div class="flex-1">Start of authority record. Enter all time values in seconds.</div>
                        <div class="mt-2">
                            Format:<br>
                                [authority-domain] [hostmaster-email-address]<br>
                                [zone-serial-number] [refresh-time] [retry-time]<br>
                                [expire-time] [negative caching TTL]<br>
                            Example:<br>
                                ns.example.net. hostmaster.example.com. 1<br>
                                7200 900 1209600 86400
                        </div>
                    </div>
                </div>
                <div v-else>
                    <app-input
                        v-model="createRecordForm.dns_name"
                        placeholder="Alias Target"
                        :error="createRecordForm.errors.get('dns_name')"
                        @updated="createRecordForm.errors.clear('dns_name')"
                        required
                    />
                    <checkbox 
                        v-model="createRecordForm.evaluate_target_health"
                        class="mt-3"
                    >Evaluate Target Health</checkbox>
                </div>          
                <div class="text-right mt-5">
                    <app-button
                        class="btn-white"
                        @click="showCreateRecordModal = false"
                        :disable="creatingRecord"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        @click="createRecord"
                        :loading="creatingRecord"
                    >Create</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showEditRecordModal"
            width="md:w-1/2 lg:w-1/3"
        >
            <template slot="header">Edit Record Set</template>
            <template slot="body">
                <app-input
                    v-model="editRecordForm.name"
                    placeholder="Name"
                    :error="editRecordForm.errors.get('name')"
                    @updated="editRecordForm.errors.clear('name')"
                    required
                    disable
                />
                <app-select
                    v-model="editRecordForm.type"
                    placeholder="Type"
                    :error="editRecordForm.errors.get('type')"
                    @updated="editRecordForm.errors.clear('type')"
                    :options="recordTypeOptions"
                    label-key="label"
                    value-key="value"
                    required
                    disable
                />                
                <div v-if="editRecordForm.alias === false">
                    <app-input
                        v-model="editRecordForm.ttl"
                        placeholder="TTL (seconds)"
                        :error="editRecordForm.errors.get('ttl')"
                        @updated="editRecordForm.errors.clear('ttl')"
                    />
                    <app-textarea
                        v-model="editRecordForm.resource_records"
                        placeholder="Value"
                        :error="editRecordForm.errors.get('resource_records')"
                        @updated="editRecordForm.errors.clear('resource_records')"
                        required
                    />
                    <div 
                        v-if="editRecordForm.type === 'A'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">IPv4 address. Enter multiple addresses on separate lines.</div>
                        <div class="flex-1 ml-5">Example:<br>
                            192.0.2.235<br>
                            198.51.100.234</div>
                    </div>
                    <div 
                        v-if="editRecordForm.type === 'CNAME'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">The domain name that you want to resolve to instead of the value in the Name field.</div>
                        <div class="flex-1 ml-5">
                            Example:<br>
                            www.example.com
                        </div>
                    </div>
                    <div 
                        v-if="editRecordForm.type === 'MX'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">A priority and a domain name that specifies a mail server. Enter multiple values on separate lines.</div>
                        <div class="flex-1 ml-5">
                            Format:<br>
                            [priority] [mail server host name]<br>
                            Example:<br>
                            10 mailserver.example.com.<br>
                            20 mailserver2.example.com.
                        </div>
                    </div>
                    <div 
                        v-if="editRecordForm.type === 'AAAA'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">IPv6 address. Enter multiple addresses on separate lines.</div>
                        <div class="flex-1 ml-5">
                            Example:<br>
                            2001:0db8:85a3:0:0:8a2e:0370:7334<br>
                            fe80:0:0:0:202:b3ff:fe1e:8329
                        </div>
                    </div>
                    <div 
                        v-if="editRecordForm.type === 'TXT'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">A text record. Enter multiple values on separate lines. Enclose text in quotation marks.</div>
                        <div class="flex-1 ml-5">
                            Example:<br>
                            "Sample Text Entries"<br>
                            "Enclose entries in quotation marks"
                        </div>
                    </div>
                    <div 
                        v-if="editRecordForm.type === 'PTR'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">The domain name that you want to return.</div>
                        <div class="flex-1 ml-5">
                            Example:<br>
                            www.example.com
                        </div>
                    </div>                    
                    <div 
                        v-if="editRecordForm.type === 'SRV'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">An SRV record. For information about SRV record format, refer to the applicable documentation. Enter multiple values on separate lines.</div>
                        <div class="flex-1 ml-5">
                            Format:<br>
                                [priority] [weight] [port] [server host name]<br>
                            Example:<br>
                                1 10 5269 xmpp-server.example.com.<br>
                                2 12 5060 sip-server.example.com.
                        </div>
                    </div>
                    <div 
                        v-if="editRecordForm.type === 'SPF'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">An SPF record. For information about SPF record format, refer to the applicable documentation. Enter multiple values on separate lines. Enclose values in quotation marks.</div>
                        <div class="flex-1 ml-5">
                            Example:<br>
                            "v=spf1 ip4:192.168.0.1/16-all"
                        </div>
                    </div>
                    <div 
                        v-if="editRecordForm.type === 'NAPTR'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">An NAPTR record. For information about NAPTR record format, refer to the applicable documentation. Enter multiple values on separate lines.</div>
                        <div class="flex-1 ml-5">
                            Format:<br>
                                [order] [preference] [flags] [services] [regexp] [replacement]<br>
                            Example:<br>
                                100 100 "U" "" "!^.*$!sip:info@bar.example.com!" .<br>
                                10 100 "S" "SIP+D2U" "" foo.example.com.
                        </div>
                    </div>
                    <div 
                        v-if="editRecordForm.type === 'CAA'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">Values that specify the certificate authorities that can issue certificates for this domain. Enter multiple values on separate lines.</div>
                        <div class="flex-1 ml-5">
                            Format:<br>
                                [flag] [tag] [value]<br>
                            Examples:<br>
                                0 issue "caa.example.com"<br>
                                0 issuewild ";"
                        </div>
                    </div>
                    <div 
                        v-if="editRecordForm.type === 'NS'"
                        class="text-gray-600 text-sm flex"
                    >
                        <div class="flex-1">The domain name of a name server. Enter multiple name servers on separate lines.</div>
                        <div class="flex-1 ml-5">
                            Examples:<br>
                                ns1.amazon.com<br>
                                ns2.amazon.org<br>
                                ns3.amazon.net<br>
                                ns4.amazon.co.uk
                        </div>
                    </div>
                    <div 
                        v-if="editRecordForm.type === 'SOA'"
                        class="text-gray-600 text-sm"
                    >
                        <div class="flex-1">Start of authority record. Enter all time values in seconds.</div>
                        <div class="mt-2">
                            Format:<br>
                                [authority-domain] [hostmaster-email-address]<br>
                                [zone-serial-number] [refresh-time] [retry-time]<br>
                                [expire-time] [negative caching TTL]<br>
                            Example:<br>
                                ns.example.net. hostmaster.example.com. 1<br>
                                7200 900 1209600 86400
                        </div>
                    </div>
                </div>
                <div v-else>
                    <app-input
                        v-model="editRecordForm.dns_name"
                        placeholder="Alias Target"
                        :error="editRecordForm.errors.get('dns_name')"
                        @updated="editRecordForm.errors.clear('dns_name')"
                        required
                    />
                    <checkbox 
                        v-model="editRecordForm.evaluate_target_health"
                        class="mt-3"
                    >Evaluate Target Health</checkbox>
                </div>          
                <div class="text-right mt-5">
                    <app-button
                        class="btn-white"
                        @click="showEditRecordModal = false"
                        :disable="updatingRecord"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        @click="updateRecord"
                        :loading="updatingRecord"
                    >Update</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    
    export default {
        data() {
            return {
                showCreateModal: false,
                showRecordsModal: false,
                showCreateRecordModal: false,
                showEditRecordModal: false,                
                creatingHostedZone: false,
                creatingRecord: false,
                updatingRecord: false,
                recordsAjaxParams: {},
                refreshTable: false,
                refreshRecordsTable: false,
                tableIsLoading: false,
                tableLoadingText: null,
                selected: {},
                createForm: new Form({
                    name: null,
                    comment: null,
                    delegation_set_id: null,
                    caller_reference: null,
                }),
                createRecordForm: new Form({                    
                    hosted_zone_id: null,
                    name: null,
                    dns_name: null,
                    evaluate_target_health: false,
                    alias: false,
                    resource_records: null,
                    ttl: 300,
                    type: null,
                }),
                editRecordForm: new Form({
                    id: null,
                    hosted_zone_id: null,
                    name: null,
                    dns_name: null,
                    evaluate_target_health: false,
                    alias: false,
                    resource_records: null,
                    ttl: 300,
                    type: null,
                }),
                delegationSets: [],
                recordTypeOptions: [
                    {
                        label: 'A – IPv4 address',
                        value: 'A',
                    },
                    {
                        label: 'CNAME – Canonical name',
                        value: 'CNAME',
                    },
                    {
                        label: 'MX – Mail exchange',
                        value: 'MX',
                    },
                    {
                        label: 'AAAA – IPv6 address',
                        value: 'AAAA',
                    },
                    {
                        label: 'TXT – Text',
                        value: 'TXT',
                    },
                    {
                        label: 'PTR – Pointer',
                        value: 'PTR',
                    },
                    {
                        label: 'SRV – Service locator',
                        value: 'SRV',
                    },
                    {
                        label: 'SPF – Sender Policy Framework',
                        value: 'SPF',
                    },
                    {
                        label: 'NAPTR – Name Authority Pointer',
                        value: 'NAPTR',
                    },
                    {
                        label: 'CAA – Certification Authority Authorization',
                        value: 'CAA',
                    },
                    {
                        label: 'NS – Name server',
                        value: 'NS',
                    },
                    {
                        label: 'SOA – Start of authority',
                        value: 'SOA',
                    },
                ],
                typeOptions: [
                    {
                        label: 'Public Hosted Zone',
                        value: 'public',
                    },
                    {
                        label: 'Private Hosted Zone for Amazon VPC',
                        value: 'private'
                    }
                ],          
                columns: [
                    {
                        title: 'Domain Name',
                        field: 'name',
                        sorter: 'string',
                        minWidth: 300,
                    },
                    {
                        title: 'Type', 
                        field: 'type', 
                        sorter: 'string',
                        headerSort: false,
                        minWidth: 200,
                    },
                    {
                        title: 'Record Set Count',
                        field: 'resource_record_set_count',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Comment',
                        field: 'comment',
                        headerSort: false,
                        minWidth: 200,
                    },
                    {
                        title: 'Hosted Zone ID',
                        field: 'id',
                        headerSort: false,
                        minWidth: 200,
                    }
                ],
                actions: [
                    {
                        label: 'Records',
                        icon: () => import('@/icons/AssignmentOutlined.vue'),
                        requiredSelect: true,
                        handler: selected => {
                            this.selected = selected[0];
                            this.showRecordsModal = true;
                            this.recordsAjaxParams = {hostedZoneId: this.selected.id}
                        }
                    },                   
                    {
                        label: 'Sync',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: this.sync,
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.showCreateModal = true;
                            this.createForm.reset();
                            this.createForm.errors.clear();
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        requiredSelect: true,
                        handler: selected => {
                            this.delete(selected[0]);
                        }
                    },
                ],
                recordsActions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        requiredSelect: true,
                        handler: selected => {
                            this.selected = selected[0];
                            this.showEditRecordModal = true;
                            this.editRecordForm.set({
                                id: this.selected.id,
                                hosted_zone_id: this.selected.hosted_zone_id,
                                name: this.selected.name,
                                type: this.selected.type,
                                resource_records: this.selected.value_for_human,
                                ttl: this.selected.ttl,
                                alias: this.selected.alias_target !== null,
                                dns_name: this.selected.alias_target,
                            });
                        }
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.showCreateRecordModal = true;
                            this.createRecordForm.reset();
                            this.createRecordForm.errors.clear();
                            this.createRecordForm.set({
                                name: this.selected.name,
                                hosted_zone_id: this.selected.id,
                            });
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        requiredSelect: true,
                        handler: selected => {
                            this.deleteRecord(selected[0]);
                        }
                    },
                ]
            }
        },
        methods: {            
            createHostedZone() {
                this.creatingHostedZone = true;
                this.$store.dispatch('aws/route53/hostedzones/store', this.createForm.payload())
                    .then(() => {
                        this.creatingHostedZone = false;
                        this.showCreateModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Hosted zone was successfully created.'
                        });
                    })
                    .catch(({ response }) => {
                        this.creatingHostedZone = false;
                        this.createForm.errors.record(response.data);
                    })
            },
            delete(selected) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete hosted zone "<strong>${selected.name}</strong>" permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('aws/route53/hostedzones/delete', selected.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Hosted zone was successfully deleted.'
                        })
                    }
                });
            },
            createRecord() {
                this.creatingRecord = true;
                this.createRecordForm.errors.clear();
                this.$store.dispatch('aws/route53/hostedzones/createRecord', this.createRecordForm.payload())
                    .then(() => {
                        this.creatingRecord = false;
                        this.refreshRecordsTable = true;
                        this.showCreateRecordModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Record was successfully created.'
                        });
                    })
                    .catch(({ response }) => {
                        this.creatingRecord = false;
                        this.createRecordForm.errors.record(response.data);
                    });
            },
            updateRecord() {
                this.updatingRecord = true;
                this.editRecordForm.errors.clear();
                this.$store.dispatch('aws/route53/hostedzones/updateRecord', this.editRecordForm.payload())
                    .then(() => {
                        this.updatingRecord = false;
                        this.refreshRecordsTable = true;
                        this.showEditRecordModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Record was successfully updated.'
                        });
                    })
                    .catch(({ response }) => {
                        this.updatingRecord = false;
                        this.editRecordForm.errors.record(response.data);
                    });
            },
            deleteRecord(selected) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete record "<strong>${selected.name}</strong>" permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('aws/route53/hostedzones/deleteRecord', selected.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshRecordsTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Record was successfully deleted.'
                        })
                    }
                });
            },
            sync() {
                this.tableIsLoading = true;
                this.tableLoadingText = 'Syncing...';
                this.$store.dispatch('aws/route53/hostedzones/sync')
                    .then(() => {
                        this.tableIsLoading = false;
                        this.refreshTable = true;
                        this.$toast.fire({
                            type: 'success',
                            title: 'Syncing complete.',
                        })
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    })
            },
            handleAjaxResponse(response) {
                this.delegationSets = response.delegation_sets;
            }           
        }
    }
</script>